// src/components/PhotoGrid.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import Masonry from "react-masonry-css";
import PhotoCard from "./PhotoCard";
import PhotoModal from "./PhotoModal";
import AdSlot from "./AdSlot";

const AD_EVERY = 12;

const BREAKPOINTS = {
  default: 4,
  1280:    3,
  860:     2,
  540:     1,
};

// Error messages based on error type
const ERROR_CONTENT = {
  server_sleep: {
    icon: "😴",
    title: "Server is waking up...",
    sub: "Our server was sleeping. It takes 30-60 seconds to wake up. Please try again!",
    btnText: "🔄 Try Again",
  },
  rate_limit: {
    icon: "⏳",
    title: "Too many requests",
    sub: "Please wait a moment and try again.",
    btnText: "🔄 Try Again",
  },
  general: {
    icon: "⚠️",
    title: "Failed to load photos",
    sub: "Something went wrong. Please check your connection and try again.",
    btnText: "🔄 Retry",
  },
};

const ErrorState = ({ error, onRetry, retrying }) => {
  const content = ERROR_CONTENT[error] || ERROR_CONTENT.general;
  return (
    <div className="state-box">
      <span className="state-box__icon">{content.icon}</span>
      <h2 className="state-box__title">{content.title}</h2>
      <p className="state-box__sub" style={{ marginBottom: 24 }}>{content.sub}</p>
      <button
        className="btn-primary"
        onClick={onRetry}
        disabled={retrying}
        style={{ minWidth: 160 }}
      >
        {retrying ? <span className="spinner" /> : content.btnText}
      </button>
    </div>
  );
};

const SkeletonGrid = () => (
  <div className="skeleton-grid" aria-busy="true" aria-label="Loading photos">
    {Array.from({ length: 12 }).map((_, i) => (
      <div key={i} className="skeleton-card"
        style={{ height: `${180 + (i % 4) * 60}px` }} />
    ))}
  </div>
);

function buildItems(photos) {
  return photos.reduce((acc, photo, i) => {
    acc.push({ type: "photo", photo });
    if ((i + 1) % AD_EVERY === 0 && i < photos.length - 1) {
      acc.push({ type: "ad", id: `ad-${i}` });
    }
    return acc;
  }, []);
}

const PhotoGrid = ({ photos, loading, error, retrying, hasMore, onLoadMore, onRetry }) => {
  const [activePhoto, setActivePhoto] = useState(null);
  const loaderRef = useRef(null);

  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting && hasMore && !loading && !error) {
      onLoadMore();
    }
  }, [hasMore, loading, error, onLoadMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "400px",
      threshold: 0,
    });
    const el = loaderRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [handleObserver]);

  // Show error state
  if (error && photos.length === 0) {
    return <ErrorState error={error} onRetry={onRetry} retrying={retrying} />;
  }

  if (!loading && photos.length === 0) {
    return (
      <div className="state-box">
        <span className="state-box__icon">🔍</span>
        <h2 className="state-box__title">No photos found</h2>
        <p className="state-box__sub">Try a different keyword or browse the curated collection.</p>
      </div>
    );
  }

  const items = buildItems(photos);

  return (
    <>
      <div className="photo-grid">
        <Masonry
          breakpointCols={BREAKPOINTS}
          className="masonry-grid"
          columnClassName="masonry-col"
        >
          {items.map((item) =>
            item.type === "photo" ? (
              <PhotoCard
                key={item.photo.id}
                photo={item.photo}
                onOpenModal={setActivePhoto}
              />
            ) : (
              <div key={item.id} className="masonry-ad-wrapper">
                <AdSlot type="inline" />
              </div>
            )
          )}
        </Masonry>

        {/* Loading spinner */}
        {loading && (
          <div className="infinite-loading">
            <span className="spinner spinner--lg" />
            <p className="infinite-loading__text">Loading photos…</p>
          </div>
        )}

        {/* Error after partial load — show small retry */}
        {error && photos.length > 0 && (
          <div className="load-more" style={{ textAlign: "center" }}>
            <p style={{ color: "var(--text-3)", fontSize: 13, marginBottom: 12 }}>
              Failed to load more. Please try again.
            </p>
            <button className="btn-outline" onClick={onRetry} disabled={retrying}>
              {retrying ? <span className="spinner" /> : "🔄 Retry"}
            </button>
          </div>
        )}

        {/* End of results */}
        {!loading && !error && !hasMore && photos.length > 0 && (
          <p className="grid-end-label">
            {photos.length.toLocaleString()} photos · End of results
          </p>
        )}

        {/* Scroll sentinel */}
        <div ref={loaderRef} className="scroll-sentinel" aria-hidden="true" />

        {/* Footer ad */}
        <AdSlot type="footer" />
      </div>

      {/* Modal */}
      {activePhoto && (
        <PhotoModal
          photo={activePhoto}
          onClose={() => setActivePhoto(null)}
        />
      )}
    </>
  );
};

export default PhotoGrid;
